/**
 * Who else is here.
 *
 * DESIGN INTENT
 *   Six people share one archive of drawings. Seeing that Sudipta has the gun
 *   housing open right now is what stops two people editing the same part in
 *   parallel — and it is most of what makes a file store feel like a place
 *   rather than a directory.
 *
 *   It is deliberately quiet: small avatars, a dot, and a tooltip. Presence
 *   that demands attention becomes something people turn off.
 */
import { useEffect, useMemo, useState } from 'react'
import { Circle } from 'lucide-react'
import {
  agePresence, othersPresent, presenceRoster, viewersOf,
  basename, type Presence, type PresenceState, type ProjectId, type Path,
} from '@lattice/core'
import { useWorkspace, personOf } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { Avatar, Tooltip } from './ui/primitives'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

/** Re-grade our copy of presence on a timer so a dot cannot go stale. */
function useAgedPresence() {
  const presence = useWorkspace(s => s.presence)
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 20_000)
    return () => clearInterval(t)
  }, [])
  return useMemo(() => agePresence(presence, Date.now()), [presence])
}

const DOT: Record<PresenceState, string> = {
  online: 'text-[var(--added)]',
  away: 'text-[var(--modified)]',
  offline: 'text-[var(--fg-subtle)]',
}

/** What to say about one person, in words. describe : Presence String -> String */
function describe(p: Presence, name: string): string {
  if (p.state === 'online') return `${name} is here`
  if (p.state === 'away') return `${name} is away`
  return p.lastSeenAt ? `${name} — last seen ${formatRelative(p.lastSeenAt)}` : `${name} has not signed in`
}

/**
 * The avatars in the top bar: everyone present except you.
 * Clicking one jumps to whatever they are looking at.
 */
export function PresenceBar() {
  const presence = useAgedPresence()
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const { go, open } = useUi()

  const others = useMemo(
    () => (me ? othersPresent(presence, me.id) : []),
    [presence, me],
  )

  if (!me) return null

  return (
    <div className="flex items-center gap-1">
      {others.length === 0 ? (
        <Tooltip label={<PresenceList presence={presence} />} side="bottom">
          <span className="px-1 text-[11px] text-[var(--fg-subtle)]">Only you</span>
        </Tooltip>
      ) : (
        <span className="flex items-center">
          {others.slice(0, 4).map((p, i) => {
            const person = personOf(roster, p.userId)
            const where = p.path ? basename(p.path) : undefined
            return (
              <Tooltip
                key={p.userId}
                side="bottom"
                label={
                  <span>
                    <span className="font-medium">{describe(p, person.name)}</span>
                    {where && <span className="block text-[var(--fg-muted)]">looking at {where}</span>}
                  </span>
                }
              >
                <button
                  onClick={() => {
                    if (!p.projectId) return
                    go({ projectId: p.projectId, view: 'files' })
                    if (p.path) open(p.path)
                  }}
                  className="relative rounded-full ring-2 ring-[var(--bg)] snap hover:z-10"
                  style={{ marginLeft: i === 0 ? 0 : -6 }}
                  aria-label={describe(p, person.name)}
                >
                  <Avatar user={person} size={22} className={cn(p.state === 'away' && 'opacity-60')} />
                  <Circle
                    className={cn('absolute -bottom-0.5 -right-0.5 h-2 w-2 fill-current', DOT[p.state])}
                    strokeWidth={0}
                  />
                </button>
              </Tooltip>
            )
          })}
          {others.length > 4 && (
            <Tooltip side="bottom" label={<PresenceList presence={presence} />}>
              <span className="ml-1 tabular text-[11px] text-[var(--fg-subtle)]">+{others.length - 4}</span>
            </Tooltip>
          )}
        </span>
      )}
    </div>
  )
}

/** The full roster with states, for the tooltip. */
function PresenceList({ presence }: { presence: ReturnType<typeof agePresence> }) {
  const roster = useWorkspace(s => s.roster)
  return (
    <span className="block min-w-44 space-y-0.5">
      {presenceRoster(presence).map(p => {
        const person = personOf(roster, p.userId)
        return (
          <span key={p.userId} className="flex items-center gap-1.5">
            <Circle className={cn('h-1.5 w-1.5 fill-current', DOT[p.state])} strokeWidth={0} />
            <span className="flex-1 truncate">{person.name}</span>
            <span className="text-[var(--fg-subtle)]">
              {p.state === 'online' ? 'here'
                : p.state === 'away' ? 'away'
                : p.lastSeenAt ? formatRelative(p.lastSeenAt) : '—'}
            </span>
          </span>
        )
      })}
    </span>
  )
}

/**
 * Who else has THIS file open, shown beside its name.
 *
 * This is the one piece of presence that changes behaviour rather than mood:
 * seeing a colleague's face on a drawing before you start editing it is the
 * cheapest possible conflict avoidance.
 */
export function FileViewers({ projectId, path }: { projectId: ProjectId; path: string }) {
  const presence = useAgedPresence()
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)

  const viewers = useMemo(
    () => (me ? viewersOf(presence, projectId, path as Path, me.id) : []),
    [presence, projectId, path, me],
  )

  if (viewers.length === 0) return null

  return (
    <span className="flex items-center gap-1">
      {viewers.map(p => {
        const person = personOf(roster, p.userId)
        return (
          <Tooltip key={p.userId} label={`${person.name} has this open too`}>
            <span><Avatar user={person} size={16} /></span>
          </Tooltip>
        )
      })}
    </span>
  )
}
